import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';
import SmallSpinner from './small-spinner';

type Icon = typeof ChevronDownIcon;

export type SelectTriggerButtonProps = {
    isLoading?: boolean;
    leftIcon?: Icon | null;
    rightIcon?: Icon | null;
    error?: boolean;
} & VariantProps<typeof selectTriggerButtonVariants> &
    React.ButtonHTMLAttributes<HTMLButtonElement>;

const selectTriggerButtonVariants = cva(
    [
        'relative flex w-full items-center',
        'min-h-8 gap-2 px-3 py-1 text-sm',
        'justify-between rounded-md border bg-white',
        'border-input ring-offset-background placeholder:text-muted-foreground',
        'focus:border-blue-500 focus:ring-blue-200 focus:ring-1',
        'active:border-blue-500 active:ring-blue-200 active:ring-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                default: 'bg-white',
                inline: [
                    'text-neutral-950',
                    'border border-transparent', // No visible border by default
                    'bg-transparent', // Transparent until hover/focus
                    'hover:bg-cool-gray-50 hover:border-transparent', // Slight background on hover
                    'focus:bg-cool-gray-50 focus:border-blue-600', // Focus ring
                    'placeholder:text-muted-foreground', // Placeholder style
                ],
            },
        },
    },
);

export const SelectTriggerButton = React.forwardRef<HTMLButtonElement, SelectTriggerButtonProps>(
    (
        {
            className,
            children,
            isLoading,
            variant,
            leftIcon: LeftIcon,
            rightIcon: RightIcon = variant === 'inline' ? null : ChevronDownIcon,
            error = false,
            ...props
        },
        ref,
    ) => (
        <button
            ref={ref}
            className={cn(
                selectTriggerButtonVariants({ variant }),
                {
                    'border-red-500 hover:border-red-600': error,
                },
                className,
            )}
            type="button"
            {...props}
        >
            {LeftIcon && <LeftIcon className="h-4 w-4 shrink-0 opacity-50" />}
            <span className="flex-1 truncate text-left">{children}</span>
            {RightIcon && <RightIcon className="h-4 w-4 shrink-0 opacity-50" />}
            {isLoading && (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-md bg-neutral-100/70">
                    <SmallSpinner className="opacity-100" />
                </div>
            )}
        </button>
    ),
);

SelectTriggerButton.displayName = 'SelectTriggerButton';
